import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import styles from "./AdminGroupInvoices.module.css";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

type InvoiceStatus =
  | "draft"
  | "sent"
  | "paid"
  | "activated"
  | "expired"
  | "canceled"
  | "activation_failed";

interface UserSearchResult {
  user_id: string;
  email: string;
  full_name: string;
  created_at?: string | null;
}

interface PreviewMember {
  user_id: string | null;
  email: string;
  full_name: string;
  is_payer: boolean;
  current_plan: string | null;
  current_period_end: string | null;
  has_supervisor_granted_subscription: boolean;
  action: string;
  blocking_reason: string | null;
}

interface PreviewResponse {
  payer_email: string;
  member_emails: string[];
  seat_count: number;
  plan: string;
  pricing: {
    unit_amount_halala: number;
    subtotal_amount_halala: number;
    discount_percentage: number;
    discount_amount_halala: number;
    total_amount_halala: number;
    currency: string;
  };
  members: PreviewMember[];
  validation_errors: string[];
  blocking_conflicts: string[];
  can_create: boolean;
}

interface GroupInvoiceMember {
  id: string;
  user_id: string;
  email: string;
  full_name: string;
  is_payer: boolean;
  activation_status: string;
  activation_error: string | null;
  activated_at: string | null;
  activation_email_sent_at?: string | null;
  subscription_before_plan?: string | null;
  subscription_before_end?: string | null;
  subscription_after_plan?: string | null;
  subscription_after_end?: string | null;
}

interface GroupInvoice {
  id: string;
  payer_email: string;
  plan: string;
  seat_count: number;
  unit_amount_halala: number;
  subtotal_amount_halala: number;
  discount_percentage: number;
  discount_amount_halala: number;
  total_amount_halala: number;
  currency: string;
  local_status: InvoiceStatus;
  moyasar_status: string | null;
  moyasar_invoice_url: string | null;
  pdf_path?: string | null;
  invoice_email_send_count: number;
  sent_at: string | null;
  paid_at: string | null;
  activated_at: string | null;
  canceled_at: string | null;
  last_error: string | null;
  created_at: string;
  metadata?: {
    dev_mode?: boolean;
    manual_status_check?: boolean;
    expires_at?: string;
  };
  members: GroupInvoiceMember[];
  member_summary: {
    activated: number;
    blocked: number;
    failed: number;
    pending: number;
  };
}

const MIGRATION_SETUP_MESSAGE =
  "Group invoice tables are not available yet. Run `supabase db push` to apply the latest migration, then restart the backend.";

const card: React.CSSProperties = {
  background: "var(--theme-surface-strong, var(--Colors-Neutral-000))",
  border: "1px solid var(--theme-border-strong, var(--Colors-Neutral-200))",
  borderRadius: "16px",
  padding: "1.5rem",
  marginBottom: "1.5rem",
  boxShadow: "0 2px 12px var(--theme-shadow, rgba(32,91,90,0.03))",
};

const panel: React.CSSProperties = {
  border: "1px solid var(--Colors-Neutral-200)",
  borderRadius: "14px",
  padding: "1rem",
  background: "linear-gradient(180deg, var(--theme-surface-strong) 0%, var(--theme-bg-subtle) 100%)",
};

const input: React.CSSProperties = {
  width: "100%",
  border: "1px solid var(--Colors-Neutral-300)",
  borderRadius: "10px",
  padding: "0.75rem 0.9rem",
  fontSize: "0.95rem",
  outline: "none",
  boxSizing: "border-box",
  background: "var(--theme-bg-elevated, var(--Colors-Neutral-000))",
  color: "var(--theme-text, var(--Colors-Primary-900))",
};

const label: React.CSSProperties = {
  display: "block",
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "var(--Colors-Primary-800)",
  marginBottom: "0.45rem",
};

const btnBase: React.CSSProperties = {
  border: "none",
  borderRadius: "10px",
  padding: "0.7rem 1rem",
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  ...btnBase,
  background: "var(--theme-primary-action, var(--Colors-Primary-700))",
  color: "var(--theme-on-primary, white)",
};

const ghostBtn: React.CSSProperties = {
  ...btnBase,
  background: "transparent",
  color: "var(--theme-control-text, var(--Colors-Primary-700))",
  border: "1px solid var(--theme-control-border, var(--Colors-Primary-600))",
};

const dangerBtn: React.CSSProperties = {
  ...btnBase,
  background: "var(--theme-danger-bg, #fff5f5)",
  color: "var(--theme-danger-text, #c53030)",
  border: "1px solid var(--theme-danger-text, #feb2b2)",
};

const subtleCard: React.CSSProperties = {
  background: "var(--Colors-Neutral-101)",
  borderRadius: "12px",
  padding: "0.9rem",
};

const statusBadge = (status: string): React.CSSProperties => {
  const styles: Record<string, { bg: string; color: string }> = {
    draft: { bg: "var(--theme-surface-soft)", color: "var(--theme-muted)" },
    sent: { bg: "var(--theme-warning-bg)", color: "var(--theme-warning-text)" },
    paid: { bg: "var(--theme-info-bg)", color: "var(--theme-info-text)" },
    activated: { bg: "var(--theme-success-bg)", color: "var(--theme-success-text)" },
    activation_failed: { bg: "var(--theme-danger-bg)", color: "var(--theme-danger-text)" },
    expired: { bg: "var(--theme-surface-soft)", color: "var(--theme-muted)" },
    canceled: { bg: "var(--theme-surface-soft)", color: "var(--theme-muted)" },
    pending: { bg: "var(--theme-warning-bg)", color: "var(--theme-warning-text)" },
    blocked: { bg: "var(--theme-danger-bg)", color: "var(--theme-danger-text)" },
    failed: { bg: "var(--theme-danger-bg)", color: "var(--theme-danger-text)" },
    activate: { bg: "var(--theme-info-bg)", color: "var(--theme-info-text)" },
    extend: { bg: "var(--theme-success-bg)", color: "var(--theme-success-text)" },
    upgrade_and_extend: { bg: "var(--theme-primary-tint-strong)", color: "var(--Colors-Primary-800)" },
  };
  const palette = styles[status] || styles.draft;
  return {
    display: "inline-block",
    padding: "0.25rem 0.6rem",
    borderRadius: "999px",
    background: palette.bg,
    color: palette.color,
    fontWeight: 700,
    fontSize: "0.75rem",
    textTransform: "capitalize",
  };
};

const formatAmount = (halala: number) => `${(halala / 100).toFixed(2)} SAR`;

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const planLabel = (plan: string) =>
  plan === "premium_plus" ? "Premium Plus" : "Premium";

const isGroupInvoiceSetupError = (value: string) =>
  /PGRST205|schema cache|public\.group_invoice/i.test(value);

const normalizeErrorMessage = (
  value: unknown,
  fallback = "Something went wrong"
) => {
  let text = fallback;

  if (value instanceof Error) {
    text = value.message || fallback;
  } else if (typeof value === "string") {
    text = value;
  } else if (value && typeof value === "object") {
    const payload = value as Record<string, unknown>;
    if (typeof payload.error === "string") {
      text = payload.error;
    } else if (typeof payload.message === "string") {
      text = payload.message;
    } else {
      try {
        text = JSON.stringify(value);
      } catch {
        text = fallback;
      }
    }
  } else if (value != null) {
    text = String(value);
  }

  if (isGroupInvoiceSetupError(text)) {
    return MIGRATION_SETUP_MESSAGE;
  }

  return text;
};

const readApiError = (payload: any, fallback: string) =>
  normalizeErrorMessage(payload?.error ?? payload?.message ?? payload, fallback);

const AdminGroupInvoices: React.FC = () => {
  const { user } = useAuthContext();

  const [plan, setPlan] = useState<"premium" | "premium_plus">("premium_plus");
  const [discountPercentage, setDiscountPercentage] = useState("0");
  const [memberSearch, setMemberSearch] = useState("");
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<UserSearchResult[]>([]);
  const [payerUserId, setPayerUserId] = useState("");
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [invoices, setInvoices] = useState<GroupInvoice[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [setupMessage, setSetupMessage] = useState<string | null>(null);

  const memberCount = selectedMembers.length;

  const orderedMembers = useMemo(
    () =>
      [...selectedMembers].sort((a, b) => {
        if (a.user_id === payerUserId) return -1;
        if (b.user_id === payerUserId) return 1;
        return (a.full_name || a.email).localeCompare(b.full_name || b.email);
      }),
    [selectedMembers, payerUserId]
  );

  const filteredSearchResults = useMemo(
    () =>
      searchResults.filter(
        (result) =>
          !selectedMembers.some((member) => member.user_id === result.user_id)
      ),
    [searchResults, selectedMembers]
  );

  const payer = useMemo(
    () => selectedMembers.find((member) => member.user_id === payerUserId) || null,
    [selectedMembers, payerUserId]
  );

  const fetchInvoices = useCallback(async () => {
    if (!user?.id) return;
    setListLoading(true);
    try {
      const res = await authenticatedFetch(
        `${apiUrl}/api/admin/group-invoices?user_id=${user.id}`
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(readApiError(data, "Failed to load group invoices"));
      }
      setInvoices(data);
      setSetupMessage(null);
    } catch (error: any) {
      const message = normalizeErrorMessage(error, "Failed to load group invoices");
      if (message === MIGRATION_SETUP_MESSAGE) {
        setSetupMessage(message);
        setInvoices([]);
      } else {
        (window as any).showNotification?.(message, "error");
      }
    } finally {
      setListLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    setPreview(null);
  }, [selectedMembers, payerUserId, plan, discountPercentage]);

  useEffect(() => {
    if (!user?.id) return;

    const query = memberSearch.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const res = await authenticatedFetch(
          `${apiUrl}/api/admin/users/search?user_id=${user.id}&query=${encodeURIComponent(
            query
          )}&limit=12`
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(readApiError(data, "Failed to search users"));
        }
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (error: any) {
        setSearchResults([]);
        setSearchError(normalizeErrorMessage(error, "Failed to search users"));
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [memberSearch, user?.id]);

  const buildPayload = () => ({
    admin_id: user?.id,
    payer_email: payer?.email || "",
    member_emails: selectedMembers.map((member) => member.email),
    plan,
    discount_percentage: Number(discountPercentage || 0),
  });

  const addMember = (candidate: UserSearchResult) => {
    setSelectedMembers((current) => {
      if (current.some((member) => member.user_id === candidate.user_id)) {
        return current;
      }
      return [...current, candidate];
    });
    if (!payerUserId) {
      setPayerUserId(candidate.user_id);
    }
    setMemberSearch("");
    setSearchResults([]);
    setSearchError(null);
  };

  const removeMember = (memberUserId: string) => {
    setSelectedMembers((current) => {
      const nextMembers = current.filter((member) => member.user_id !== memberUserId);
      if (payerUserId === memberUserId) {
        setPayerUserId(nextMembers[0]?.user_id || "");
      }
      return nextMembers;
    });
  };

  const handlePreview = async () => {
    if (!user?.id) return;
    setPreviewLoading(true);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/admin/group-invoices/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(readApiError(data, "Failed to generate preview"));
      }
      setPreview(data);
      setSetupMessage(null);
      if (data.can_create) {
        (window as any).showNotification?.("Preview updated", "success");
      }
    } catch (error: any) {
      const message = normalizeErrorMessage(error, "Failed to generate preview");
      if (message === MIGRATION_SETUP_MESSAGE) {
        setSetupMessage(message);
      }
      (window as any).showNotification?.(message, "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreateAndSend = async () => {
    if (!user?.id) return;
    setSubmitting(true);
    try {
      const createRes = await authenticatedFetch(`${apiUrl}/api/admin/group-invoices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const createData = await createRes.json();
      if (!createRes.ok) {
        throw new Error(
          readApiError(createData, "Failed to create group invoice draft")
        );
      }

      const sendRes = await authenticatedFetch(
        `${apiUrl}/api/admin/group-invoices/${createData.id}/send`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ admin_id: user.id }),
        }
      );
      const sendData = await sendRes.json();
      if (!sendRes.ok) {
        await fetchInvoices();
        throw new Error(readApiError(sendData, "Draft created, but sending failed"));
      }

      (window as any).showNotification?.("Invoice created and sent", "success");
      setSelectedMembers([]);
      setPayerUserId("");
      setDiscountPercentage("0");
      setPlan("premium_plus");
      setMemberSearch("");
      setSearchResults([]);
      setSearchError(null);
      setPreview(null);
      setSetupMessage(null);
      await fetchInvoices();
    } catch (error: any) {
      const message = normalizeErrorMessage(
        error,
        "Failed to create and send group invoice"
      );
      if (message === MIGRATION_SETUP_MESSAGE) {
        setSetupMessage(message);
      }
      (window as any).showNotification?.(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const runInvoiceAction = async (invoiceId: string, action: string) => {
    if (!user?.id) return;
    setActionLoading(`${invoiceId}:${action}`);
    try {
      const res = await authenticatedFetch(
        `${apiUrl}/api/admin/group-invoices/${invoiceId}/${action}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ admin_id: user.id }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Failed to ${action} invoice`);
      }
      setInvoices((current) =>
        current.map((invoice) => (invoice.id === data.id ? data : invoice))
      );
      setSetupMessage(null);
      await fetchInvoices();
      const verb =
        action === "refresh"
          ? "refreshed"
          : action === "resend"
            ? "resent"
            : action === "cancel"
              ? "canceled"
              : "sent";
      (window as any).showNotification?.(`Invoice ${verb}`, "success");
    } catch (error: any) {
      const message = normalizeErrorMessage(error, `Failed to ${action} invoice`);
      if (message === MIGRATION_SETUP_MESSAGE) {
        setSetupMessage(message);
      }
      (window as any).showNotification?.(message, "error");
    } finally {
      setActionLoading(null);
    }
  };

  const setupBlocked = Boolean(setupMessage);
  const showSearchPanel =
    memberSearch.trim().length >= 2 ||
    searchLoading ||
    filteredSearchResults.length > 0 ||
    Boolean(searchError);

  return (
    <div className={styles.pageShell}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: "1.5rem",
              color: "var(--Colors-Primary-800)",
            }}
          >
            Group Invoices
          </h2>
          <p style={{ margin: "0.35rem 0 0", color: "var(--Colors-Neutral-600)" }}>
            Create Moyasar-hosted invoices for discounted group subscriptions and
            manage their lifecycle.
          </p>
        </div>
        <button style={ghostBtn} onClick={fetchInvoices} disabled={listLoading}>
          {listLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {setupMessage && (
        <div
          style={{
            ...card,
            background: "var(--theme-warning-bg)",
            borderColor: "var(--theme-warning-text)",
            marginBottom: "1.25rem",
          }}
        >
          <div style={{ fontWeight: 700, color: "var(--theme-warning-text)", marginBottom: "0.45rem" }}>
            Database setup required
          </div>
          <div style={{ color: "var(--theme-warning-text)", lineHeight: 1.6 }}>
            {setupMessage} After that, refresh this page and the tab will load
            normally.
          </div>
        </div>
      )}

      <div style={card}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          <div>
            <label style={label}>Plan</label>
            <select
              style={input}
              value={plan}
              onChange={(e) =>
                setPlan(e.target.value as "premium" | "premium_plus")
              }
            >
              <option value="premium">Premium</option>
              <option value="premium_plus">Premium Plus</option>
            </select>
          </div>
          <div>
            <label style={label}>Discount Percentage</label>
            <input
              style={input}
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(e.target.value)}
              placeholder="0"
            />
          </div>
          <div>
            <label style={label}>Selected Members</label>
            <div style={{ ...input, background: "var(--Colors-Neutral-100)" }}>
              {memberCount}
            </div>
          </div>
          <div>
            <label style={label}>Invoice Recipient</label>
            <div
              style={{
                ...input,
                background: "var(--Colors-Neutral-100)",
                color: payer ? "inherit" : "var(--Colors-Neutral-500)",
              }}
            >
              {payer?.email || "Choose the payer from the selected members"}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(280px, 1.1fr) minmax(320px, 1fr)",
            gap: "1rem",
            alignItems: "start",
          }}
        >
          <div style={panel}>
            <label style={label}>Search Members</label>
            <input
              style={input}
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              placeholder="Search by email or full name"
            />
            <p
              style={{
                margin: "0.55rem 0 0",
                color: "var(--Colors-Neutral-500)",
                fontSize: "0.85rem",
              }}
            >
              Search existing C-Shaped users, then add them to the group. Type at
              least 2 characters.
            </p>

            {showSearchPanel && (
              <div
                style={{
                  marginTop: "0.85rem",
                  border: "1px solid var(--Colors-Neutral-200)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  background: "var(--theme-bg-elevated)",
                }}
              >
                {searchLoading ? (
                  <div style={{ padding: "0.9rem", color: "var(--Colors-Neutral-500)" }}>
                    Searching users...
                  </div>
                ) : searchError ? (
                  <div style={{ padding: "0.9rem", color: "var(--theme-danger-text)" }}>{searchError}</div>
                ) : filteredSearchResults.length === 0 ? (
                  <div style={{ padding: "0.9rem", color: "var(--Colors-Neutral-500)" }}>
                    No matching users found.
                  </div>
                ) : (
                  filteredSearchResults.map((result) => (
                    <button
                      key={result.user_id}
                      type="button"
                      onClick={() => addMember(result)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "var(--theme-bg-elevated)",
                        borderBottom: "1px solid var(--Colors-Neutral-100)",
                        padding: "0.9rem 1rem",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "1rem",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, color: "var(--Colors-Primary-800)" }}>
                          {result.full_name || "Unnamed user"}
                        </div>
                        <div
                          style={{
                            color: "var(--Colors-Neutral-600)",
                            fontSize: "0.85rem",
                            marginTop: "0.15rem",
                          }}
                        >
                          {result.email}
                        </div>
                      </div>
                      <div
                        style={{
                          color: "var(--Colors-Primary-700)",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Add
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div style={panel}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
                alignItems: "center",
                marginBottom: "0.75rem",
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: "var(--Colors-Primary-800)" }}>
                  Selected Members
                </div>
                <div style={{ color: "var(--Colors-Neutral-500)", fontSize: "0.85rem" }}>
                  Pick one selected member as the invoice recipient.
                </div>
              </div>
              <span style={statusBadge("draft")}>{memberCount} selected</span>
            </div>

            {orderedMembers.length === 0 ? (
              <div
                style={{
                  padding: "1rem",
                  borderRadius: "12px",
                  background: "var(--theme-bg-elevated)",
                  color: "var(--Colors-Neutral-500)",
                  border: "1px dashed var(--Colors-Neutral-300)",
                }}
              >
                No members selected yet.
              </div>
            ) : (
              <div style={{ display: "grid", gap: "0.75rem" }}>
                {orderedMembers.map((member) => (
                  <div
                    key={member.user_id}
                    style={{
                      background: "var(--theme-bg-elevated)",
                      border: "1px solid var(--Colors-Neutral-200)",
                      borderRadius: "12px",
                      padding: "0.85rem 0.95rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "1rem",
                        alignItems: "flex-start",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          gap: "0.75rem",
                          alignItems: "flex-start",
                          cursor: "pointer",
                          flex: 1,
                        }}
                      >
                        <input
                          type="radio"
                          name="group-invoice-payer"
                          checked={payerUserId === member.user_id}
                          onChange={() => setPayerUserId(member.user_id)}
                          style={{ marginTop: "0.25rem" }}
                        />
                        <div>
                          <div
                            style={{
                              fontWeight: 700,
                              color: "var(--Colors-Primary-800)",
                            }}
                          >
                            {member.full_name || "Unnamed user"}
                          </div>
                          <div
                            style={{
                              color: "var(--Colors-Neutral-600)",
                              fontSize: "0.85rem",
                              marginTop: "0.15rem",
                            }}
                          >
                            {member.email}
                          </div>
                        </div>
                      </label>
                      <button
                        type="button"
                        onClick={() => removeMember(member.user_id)}
                        style={{
                          ...ghostBtn,
                          padding: "0.45rem 0.7rem",
                          fontSize: "0.82rem",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    {payerUserId === member.user_id && (
                      <div style={{ marginTop: "0.65rem" }}>
                        <span style={statusBadge("paid")}>Invoice recipient</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "1rem" }}>
          <button
            style={ghostBtn}
            onClick={handlePreview}
            disabled={previewLoading || submitting || setupBlocked}
          >
            {previewLoading ? "Previewing..." : "Preview"}
          </button>
          <button
            style={primaryBtn}
            onClick={handleCreateAndSend}
            disabled={submitting || setupBlocked}
          >
            {submitting ? "Creating..." : "Create Draft and Send"}
          </button>
        </div>
      </div>

      {preview && (
        <div style={card}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap",
              marginBottom: "1rem",
            }}
          >
            <div>
              <h3 style={{ margin: 0, color: "var(--Colors-Primary-800)" }}>Preview</h3>
              <p style={{ margin: "0.35rem 0 0", color: "var(--Colors-Neutral-600)" }}>
                {planLabel(preview.plan)} · {preview.seat_count} members · recipient{" "}
                {preview.payer_email}
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "0.9rem", color: "var(--Colors-Neutral-600)" }}>
                Final total
              </div>
              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 800,
                  color: "var(--Colors-Primary-800)",
                }}
              >
                {preview.pricing
                  ? formatAmount(preview.pricing.total_amount_halala)
                  : "-"}
              </div>
            </div>
          </div>

          {preview.pricing && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "0.75rem",
                marginBottom: "1rem",
              }}
            >
              <div style={subtleCard}>
                <div style={{ color: "var(--Colors-Neutral-500)", fontSize: "0.8rem" }}>
                  Unit price
                </div>
                <div style={{ fontWeight: 700 }}>
                  {formatAmount(preview.pricing.unit_amount_halala)}
                </div>
              </div>
              <div style={subtleCard}>
                <div style={{ color: "var(--Colors-Neutral-500)", fontSize: "0.8rem" }}>
                  Subtotal
                </div>
                <div style={{ fontWeight: 700 }}>
                  {formatAmount(preview.pricing.subtotal_amount_halala)}
                </div>
              </div>
              <div style={subtleCard}>
                <div style={{ color: "var(--Colors-Neutral-500)", fontSize: "0.8rem" }}>
                  Discount
                </div>
                <div style={{ fontWeight: 700 }}>
                  {preview.pricing.discount_percentage.toFixed(2)}% (
                  {formatAmount(preview.pricing.discount_amount_halala)})
                </div>
              </div>
            </div>
          )}

          {preview.validation_errors.length > 0 && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "0.9rem",
                borderRadius: "12px",
                background: "var(--theme-danger-bg)",
                color: "var(--theme-danger-text)",
              }}
            >
              <strong>Validation issues</strong>
              <ul style={{ margin: "0.5rem 0 0 1rem" }}>
                {preview.validation_errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {preview.blocking_conflicts.length > 0 && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "0.9rem",
                borderRadius: "12px",
                background: "var(--theme-warning-bg)",
                color: "var(--theme-warning-text)",
              }}
            >
              <strong>Blocking conflicts</strong>
              <ul style={{ margin: "0.5rem 0 0 1rem" }}>
                {preview.blocking_conflicts.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      fontSize: "0.78rem",
                      color: "var(--Colors-Neutral-500)",
                      paddingBottom: "0.5rem",
                    }}
                  >
                    Member
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      fontSize: "0.78rem",
                      color: "var(--Colors-Neutral-500)",
                      paddingBottom: "0.5rem",
                    }}
                  >
                    Current plan
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      fontSize: "0.78rem",
                      color: "var(--Colors-Neutral-500)",
                      paddingBottom: "0.5rem",
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {preview.members.map((member) => (
                  <tr key={member.email}>
                    <td
                      style={{
                        padding: "0.55rem 0",
                        borderTop: "1px solid var(--Colors-Neutral-100)",
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>
                        {member.full_name || member.email}
                      </div>
                      <div
                        style={{
                          color: "var(--Colors-Neutral-500)",
                          fontSize: "0.85rem",
                        }}
                      >
                        {member.email} {member.is_payer ? "· payer" : ""}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "0.55rem 0",
                        borderTop: "1px solid var(--Colors-Neutral-100)",
                      }}
                    >
                      {member.current_plan
                        ? `${planLabel(member.current_plan)} until ${formatDate(
                            member.current_period_end
                          )}`
                        : "No active subscription"}
                    </td>
                    <td
                      style={{
                        padding: "0.55rem 0",
                        borderTop: "1px solid var(--Colors-Neutral-100)",
                      }}
                    >
                      <span style={statusBadge(member.action)}>
                        {member.action.replace(/_/g, " ")}
                      </span>
                      {member.blocking_reason && (
                        <div
                          style={{
                            color: "var(--theme-danger-text)",
                            fontSize: "0.8rem",
                            marginTop: "0.35rem",
                          }}
                        >
                          {member.blocking_reason}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: "var(--Colors-Primary-800)" }}>
              Existing group invoices
            </h3>
            <p style={{ margin: "0.35rem 0 0", color: "var(--Colors-Neutral-600)" }}>
              Drafts, sent invoices, payment progress, and activation results.
            </p>
          </div>
        </div>

        {setupMessage ? (
          <div
            style={{
              padding: "1rem",
              borderRadius: "12px",
              background: "var(--theme-warning-bg)",
              color: "var(--theme-warning-text)",
            }}
          >
            {setupMessage}
          </div>
        ) : listLoading ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "var(--Colors-Neutral-500)" }}>
            Loading invoices...
          </div>
        ) : invoices.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "var(--Colors-Neutral-500)" }}>
            No group invoices yet.
          </div>
        ) : (
          <div style={{ display: "grid", gap: "1rem" }}>
            {invoices.map((invoice) => {
              const busy = (action: string) => actionLoading === `${invoice.id}:${action}`;
              return (
                <details
                  key={invoice.id}
                  style={{
                    border: "1px solid var(--Colors-Neutral-200)",
                    borderRadius: "14px",
                    padding: "1rem 1.1rem",
                  }}
                >
                  <summary style={{ listStyle: "none", cursor: "pointer" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "1rem",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            gap: "0.5rem",
                            alignItems: "center",
                            flexWrap: "wrap",
                            marginBottom: "0.35rem",
                          }}
                        >
                          <span style={{ fontWeight: 700 }}>{planLabel(invoice.plan)}</span>
                          <span style={statusBadge(invoice.local_status)}>
                            {invoice.local_status.replace(/_/g, " ")}
                          </span>
                          {invoice.moyasar_status && (
                            <span style={statusBadge("paid")}>
                              Moyasar: {invoice.moyasar_status}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            color: "var(--Colors-Neutral-600)",
                            fontSize: "0.9rem",
                          }}
                        >
                          {invoice.payer_email} · {invoice.seat_count} members · created{" "}
                          {formatDate(invoice.created_at)}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div
                          style={{
                            fontSize: "0.8rem",
                            color: "var(--Colors-Neutral-500)",
                          }}
                        >
                          Total
                        </div>
                        <div style={{ fontWeight: 800, fontSize: "1.1rem" }}>
                          {formatAmount(invoice.total_amount_halala)}
                        </div>
                      </div>
                    </div>
                  </summary>

                  <div
                    style={{
                      marginTop: "1rem",
                      borderTop: "1px solid var(--Colors-Neutral-100)",
                      paddingTop: "1rem",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                        gap: "0.75rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <div>
                        <strong>Subtotal:</strong> {formatAmount(invoice.subtotal_amount_halala)}
                      </div>
                      <div>
                        <strong>Discount:</strong> {invoice.discount_percentage.toFixed(2)}%
                      </div>
                      <div>
                        <strong>Sent:</strong> {formatDate(invoice.sent_at)}
                      </div>
                      <div>
                        <strong>Paid:</strong> {formatDate(invoice.paid_at)}
                      </div>
                      <div>
                        <strong>Activated:</strong> {formatDate(invoice.activated_at)}
                      </div>
                      <div>
                        <strong>Email sends:</strong> {invoice.invoice_email_send_count || 0}
                      </div>
                      <div>
                        <strong>Receipt PDF:</strong> {invoice.pdf_path || "-"}
                      </div>
                    </div>

                    {invoice.last_error && (
                      <div
                        style={{
                          marginBottom: "1rem",
                          padding: "0.8rem",
                          borderRadius: "12px",
                          background: "var(--theme-warning-bg)",
                          color: "var(--theme-warning-text)",
                        }}
                      >
                        {invoice.last_error}
                      </div>
                    )}

                    {invoice.metadata?.manual_status_check && (
                      <div
                        style={{
                          marginBottom: "1rem",
                          padding: "0.8rem",
                          borderRadius: "12px",
                          background: "var(--theme-success-bg)",
                          color: "var(--theme-success-text)",
                        }}
                      >
                        Dev mode is active for this invoice. Payment status is verified
                        when the payer returns to the app or when you click Refresh.
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: "0.75rem",
                        flexWrap: "wrap",
                        marginBottom: "1rem",
                      }}
                    >
                      {invoice.local_status === "draft" && (
                        <button
                          style={primaryBtn}
                          onClick={() => runInvoiceAction(invoice.id, "send")}
                          disabled={busy("send")}
                        >
                          {busy("send") ? "Sending..." : "Send"}
                        </button>
                      )}
                      {invoice.local_status === "sent" && (
                        <button
                          style={ghostBtn}
                          onClick={() => runInvoiceAction(invoice.id, "resend")}
                          disabled={busy("resend")}
                        >
                          {busy("resend") ? "Resending..." : "Resend Email"}
                        </button>
                      )}
                      {invoice.local_status !== "canceled" &&
                        invoice.local_status !== "activated" && (
                          <button
                            style={ghostBtn}
                            onClick={() => runInvoiceAction(invoice.id, "refresh")}
                            disabled={busy("refresh")}
                          >
                            {busy("refresh") ? "Refreshing..." : "Refresh"}
                          </button>
                        )}
                      {(invoice.local_status === "draft" ||
                        invoice.local_status === "sent") && (
                        <button
                          style={dangerBtn}
                          onClick={() => runInvoiceAction(invoice.id, "cancel")}
                          disabled={busy("cancel")}
                        >
                          {busy("cancel") ? "Canceling..." : "Cancel"}
                        </button>
                      )}
                      {invoice.moyasar_invoice_url && (
                        <a
                          href={invoice.moyasar_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            ...ghostBtn,
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          Open Pay Link
                        </a>
                      )}
                    </div>

                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th
                              style={{
                                textAlign: "left",
                                fontSize: "0.78rem",
                                color: "var(--Colors-Neutral-500)",
                                paddingBottom: "0.5rem",
                              }}
                            >
                              Member
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                fontSize: "0.78rem",
                                color: "var(--Colors-Neutral-500)",
                                paddingBottom: "0.5rem",
                              }}
                            >
                              Activation
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                fontSize: "0.78rem",
                                color: "var(--Colors-Neutral-500)",
                                paddingBottom: "0.5rem",
                              }}
                            >
                              Result
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.members.map((member) => (
                            <tr key={member.id}>
                              <td
                                style={{
                                  padding: "0.65rem 0",
                                  borderTop: "1px solid var(--Colors-Neutral-100)",
                                }}
                              >
                                <div style={{ fontWeight: 600 }}>
                                  {member.full_name || member.email}
                                </div>
                                <div
                                  style={{
                                    color: "var(--Colors-Neutral-500)",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {member.email} {member.is_payer ? "· payer" : ""}
                                </div>
                              </td>
                              <td
                                style={{
                                  padding: "0.65rem 0",
                                  borderTop: "1px solid var(--Colors-Neutral-100)",
                                }}
                              >
                                <span style={statusBadge(member.activation_status)}>
                                  {member.activation_status}
                                </span>
                                <div
                                  style={{
                                    color: "var(--Colors-Neutral-500)",
                                    fontSize: "0.82rem",
                                    marginTop: "0.35rem",
                                  }}
                                >
                                  Activated: {formatDate(member.activated_at)}
                                </div>
                              </td>
                              <td
                                style={{
                                  padding: "0.65rem 0",
                                  borderTop: "1px solid var(--Colors-Neutral-100)",
                                }}
                              >
                                {member.activation_error ? (
                                  <div style={{ color: "var(--theme-danger-text)", fontSize: "0.85rem" }}>
                                    {member.activation_error}
                                  </div>
                                ) : member.subscription_after_end ? (
                                  <div style={{ fontSize: "0.85rem" }}>
                                    {member.subscription_after_plan
                                      ? planLabel(member.subscription_after_plan)
                                      : planLabel(invoice.plan)}{" "}
                                    until {formatDate(member.subscription_after_end)}
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      color: "var(--Colors-Neutral-500)",
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    Pending payment
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </details>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGroupInvoices;
